class ExitIntentModal extends HTMLElement {
  constructor() {
    super();

    // Configuration from data attributes
    this.config = {
      enableMouseLeave: this.dataset.mouseLeave === 'true',
      mouseLeaveDelay: parseInt(this.dataset.mouseLeaveDelay) || 1000,
      enableTimeDelay: this.dataset.timeDelay === 'true',
      timeDelay: parseInt(this.dataset.timeDelayMs) || 30000,
      enableScrollDepth: this.dataset.scrollDepth === 'true',
      scrollDepthPercent: parseInt(this.dataset.scrollPercent) || 50,
      frequency: this.dataset.frequency || 'daily',
      closeOnSuccess: this.dataset.closeOnSuccess === 'true',
      forceShow: this.dataset.forceShow === 'true',
      sectionId: this.dataset.sectionId,
      modalIdentifier: this.dataset.modalIdentifier || this.dataset.sectionId,
    };

    // State management
    this.hasShown = false;
    this.timerId = null;
    this.mouseLeaveTimer = null;
    this.modalDialog = null;

    // Bound event handlers
    this.handleMouseLeave = null;
    this.handleScroll = null;
  }

  connectedCallback() {
    // Wait for modal-dialog custom element to be defined, then initialize
    if (customElements.get('modal-dialog')) {
      this.initialize();
    } else {
      customElements.whenDefined('modal-dialog').then(() => {
        this.initialize();
      });
    }
  }

  initialize() {
    // Query by ID since ModalDialog moves itself to document.body
    const modalId = 'ExitIntentModal-' + this.config.sectionId;
    this.modalDialog = document.getElementById(modalId);

    if (!this.modalDialog) {
      console.warn('ExitIntentModal: modal-dialog not found with ID:', modalId);
      return;
    }

    // When the theme editor re-renders the section, this element connects before its modal-dialog child is upgraded
    customElements.upgrade(this.modalDialog);

    this.runInitialization();
  }

  runInitialization() {
    // If force show is enabled, show immediately
    if (this.config.forceShow) {
      this.showModal('force-show');
      return;
    }

    // Check if we're in the theme editor - don't auto-trigger
    if (window.Shopify && window.Shopify.designMode) {
      return;
    }

    // Don't initialize if already shown based on frequency
    if (this.shouldSkipBasedOnFrequency()) {
      return;
    }

    this.initializeTriggers();
    this.observeFormSuccess();
  }

  disconnectedCallback() {
    this.cleanupTriggers();
  }

  shouldSkipBasedOnFrequency() {
    const storageKey = `exit-intent-shown-${this.config.modalIdentifier}`;

    // Always show in testing mode
    if (this.config.frequency === 'always') {
      return false;
    }

    let lastShown;
    try {
      lastShown = localStorage.getItem(storageKey);
    } catch (e) {
      // localStorage not available, fallback to always showing
      return false;
    }

    if (!lastShown) {
      return false;
    }

    const lastShownTime = parseInt(lastShown);
    const now = Date.now();
    const elapsed = now - lastShownTime;

    switch (this.config.frequency) {
      case 'once':
        // Already shown this session, never show again
        return true;
      case 'daily':
        // Show if more than 24 hours have passed
        return elapsed < 86400000; // 24 hours in milliseconds
      case 'weekly':
        // Show if more than 7 days have passed
        return elapsed < 604800000; // 7 days in milliseconds
      default:
        return false;
    }
  }

  initializeTriggers() {
    // Mouse leave detection (desktop only)
    if (this.config.enableMouseLeave && window.innerWidth >= 990) {
      this.handleMouseLeave = this.onMouseLeave.bind(this);
      document.addEventListener('mouseout', this.handleMouseLeave);
    }

    // Time delay trigger
    if (this.config.enableTimeDelay) {
      this.timerId = setTimeout(() => {
        this.showModal('time-delay');
      }, this.config.timeDelay);
    }

    // Scroll depth trigger
    if (this.config.enableScrollDepth) {
      this.handleScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.handleScroll, { passive: true });
    }
  }

  cleanupTriggers() {
    if (this.handleMouseLeave) {
      document.removeEventListener('mouseout', this.handleMouseLeave);
      this.handleMouseLeave = null;
    }

    if (this.handleScroll) {
      window.removeEventListener('scroll', this.handleScroll);
      this.handleScroll = null;
    }

    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }

    if (this.mouseLeaveTimer) {
      clearTimeout(this.mouseLeaveTimer);
      this.mouseLeaveTimer = null;
    }
  }

  onMouseLeave(event) {
    // Check if mouse is leaving from top of viewport
    // relatedTarget will be null when leaving the document
    if (event.clientY <= 0 && event.relatedTarget == null) {
      // Add delay to prevent false positives
      this.mouseLeaveTimer = setTimeout(() => {
        this.showModal('mouse-leave');
      }, this.config.mouseLeaveDelay);
    }
  }

  onScroll() {
    // Calculate scroll percentage
    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;

    // Avoid division by zero on very short pages
    if (scrollHeight <= 0) {
      return;
    }

    const scrollPercent = (window.scrollY / scrollHeight) * 100;

    if (scrollPercent >= this.config.scrollDepthPercent) {
      this.showModal('scroll-depth');
    }
  }

  showModal(trigger) {
    // Only show once
    if (this.hasShown) {
      return;
    }

    this.hasShown = true;
    this.cleanupTriggers();

    // Record show timestamp
    const storageKey = `exit-intent-shown-${this.config.modalIdentifier}`;
    try {
      localStorage.setItem(storageKey, Date.now().toString());
    } catch (e) {
      // localStorage not available, continue anyway
      console.warn('ExitIntentModal: localStorage not available');
    }

    // Track trigger type (optional for analytics)
    this.dataset.triggerType = trigger;

    // Show the modal using existing ModalDialog
    if (this.modalDialog) {
      this.modalDialog.show();
    }
  }

  observeFormSuccess() {
    // Watch for form success and optionally close modal
    if (!this.config.closeOnSuccess || !this.modalDialog) {
      return;
    }

    // Use MutationObserver to watch for success message appearing
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.classList && node.classList.contains('newsletter-form__message--success')) {
              // Close modal after 2 seconds to let user see success message
              setTimeout(() => {
                if (this.modalDialog) {
                  this.modalDialog.hide();
                }
              }, 2000);
            }
          }
        });
      });
    });

    // Observe the modal content for changes
    observer.observe(this.modalDialog, {
      childList: true,
      subtree: true,
    });

    // Store observer for cleanup
    this.formSuccessObserver = observer;
  }
}

customElements.define('exit-intent-modal', ExitIntentModal);
